<template>
    <div>
        <div class="product-list-filters__bookmark2"></div>
        <div
            ref="bookmark"
            class="product-list-filters__bookmark"
            :class="{ 'product-list-filters__bookmark--sticky': isFilterSticky }"
        ></div>
        <div
            id="product-list-filters"
            ref="productListFilters"
            class="product-list-filters"
            :class="{ 'product-list-filters--sticky': isFilterSticky }"
        >
            <div class="product-list-filters__container">
                <div ref="filtersContainer" class="product-list-filters__filters">
                    <template v-for="(filter, filterId) in sidebarFilters" :key="filterId">
                        <div
                            v-if="filter.type !== 'minRating' || hasValidRatingItems"
                            class="product-list-filters__filter"
                            :class="{ 'product-list-filters__filter--visible': filterId < visibleFiltersCount }"
                        >
                            <div
                                class="product-list-filters__title"
                                :class="{ 'product-list-filters__title--active': productListFilterStore.openFilterTitle === filter.title }"
                                @click="toggleFilter(filter.title)"
                            >
                                <span class="product-list-filters__title__text">{{ filter.title || $t('sideBar.quickFilters') }}</span>
                                <span
                                    v-if="activeFilter(filter) > 0"
                                    class="product-list-filters__title__count"
                                    v-html="activeFilter(filter)"
                                />
                            </div>
                            <div
                                class="product-list-filters__content"
                                :class="{
                                    'product-list-filters__content--opened': productListFilterStore.openFilterTitle === filter.title,
                                }"
                            >
                                <ObjectsFilterToolsRangeSlider
                                    v-if="['number', 'range'].includes(filter.type) && filter.selection?.from !== null"
                                    :filter="filter"
                                />
                                <div
                                    v-else-if="filter.type === 'minRating' && hasValidRatingItems"
                                    class="filter-checkbox"
                                    data-cy="filter-checkbox"
                                >
                                    <component
                                        :is="canBeAnchorTag(item.url) ? 'a' : 'span'"
                                        v-for="(item, index) in (filter.items ?? []).filter((item) => item.count > 0)"
                                        :key="index"
                                        :href="canBeAnchorTag(item.url) ? `${item.url}#product-list` : null"
                                        class="checkbox"
                                        :class="{ 'is-active': item.isActive }"
                                        :aria-label="item.title"
                                        @click.prevent="redirectToUrl(item.url)"
                                    >
                                        <ObjectsRating :rating="item.rating" :total="item.count" :total-text="item.title" size="size-xl" />
                                    </component>
                                </div>
                                <div v-else-if="filter.type === 'nutraRating'" class="filter-checkbox" data-cy="filter-checkbox">
                                    <component
                                        :is="canBeAnchorTag(item.url) ? 'a' : 'span'"
                                        v-for="(item, index) in (filter.items ?? []).filter((item) => item.count > 0)"
                                        :key="index"
                                        :href="canBeAnchorTag(item.url) ? `${item.url}#product-list` : null"
                                        class="checkbox"
                                        :class="{ 'is-active': item.isActive }"
                                        :aria-label="item.title"
                                        @click.prevent="redirectToUrl(item.url)"
                                    >
                                        <span>{{ item.title }}</span
                                        >&nbsp;({{ item.count }})
                                    </component>
                                </div>
                                <template v-else-if="filter.type === 'enum'">
                                    <input
                                        :id="'form-input-search' + filterId"
                                        v-model="inputSearch[filterId]"
                                        class="product-list-filters__search-input"
                                        :placeholder="$t('sideBar.search')"
                                        type="text"
                                    />

                                    <div
                                        class="product-list-filters__body"
                                        :class="{
                                            notranslate: [$t('search.searchBrands'), $t('search.searchProductLines')].includes(
                                                filter.title
                                            ),
                                        }"
                                    >
                                        <div class="filter-checkbox">
                                            <component
                                                :is="canBeAnchorTag(option.url) ? 'a' : 'span'"
                                                v-for="(option, index) in filteredList(filter, filterId)"
                                                :id="option.id ? `option-${option.id}` : null"
                                                :key="index"
                                                :href="canBeAnchorTag(option.url) ? `${option.url}#product-list` : null"
                                                class="checkbox"
                                                :class="{
                                                    'is-active': option.isActive,
                                                    disabled: option.count === 0,
                                                }"
                                                :style="{ color: option.textColor }"
                                                :data-cy="`${option.title}`"
                                                tabindex="0"
                                                @click.prevent="redirectToUrl(option.url)"
                                            >
                                                <img
                                                    v-if="option.image"
                                                    :src="`https://www.pilulka.cz${option.image}`"
                                                    height="15"
                                                    class="pr-1 d-inline-block"
                                                    style="max-height: 20px; padding-right: 5px"
                                                />
                                                <span>{{ option.title }}</span
                                                >&nbsp;({{ $numberFormat(option.count) }})
                                            </component>

                                            <div v-if="!filteredList(filter, filterId).length">
                                                <div class="alert alert-danger px-5" role="alert">{{ $t('sideBar.notFoundItems') }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </template>
                    <button
                        v-if="sidebarFilters.length > dynamicFiltersCount"
                        class="product-list-filters__more-filters-button"
                        :class="{ 'product-list-filters__more-filters-button--arrow-up': productListFilterStore.showMoreFilters }"
                        @click="toggleMoreFilters"
                    >
                        {{ $t(productListFilterStore.showMoreFilters ? 'hideMoreFilters' : 'showMoreFilters') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import Filter from '~/models/Filters/Filter'
import FilterOption from '~/models/Filters/FilterOption'
import { useProductListFilterStore } from '~/stores/productListFilter'
import { removeDiacritics } from '~/utils/removeDiacritics'

const props = defineProps<{
    // sidebarFilterTitle: {
    //     type: String,
    //     default: 'sideBar.filterProduct',
    // },
    // sidebarFilterSubmitLabel: {
    //     type: String,
    //     default: 'sideBar.send',
    // },
    // sidebarSortTitle: {
    //     type: String,
    //     default: 'sideBar.sortProduct',
    // },
    // sidebarSortSubmitLabel: {
    //     type: String,
    //     default: 'sideBar.viewProduct',
    // },
    sidebarFilters: Filter[]
    // sidebarCategories: {
    //     type: Array as PropType<{ title: string; count: number; shopUrlPath: string }[]>,
    //     required: false,
    //     default: Infinity,
    // },
    // basePath: {
    //     type: String,
    //     required: true,
    // },
    // isLocationFilterActive: {
    //     type: Boolean,
    //     default: false,
    // },
    // sorts: {
    //     type: Array as PropType<{ id: number; name: string; url: string }[]>,
    //     default: Infinity,
    // },
    // selectedFilters: {
    //     type: Array,
    //     default: Infinity,
    // },
    // count: {
    //     type: Number,
    //     required: true,
    // },
    // defaultSort: {
    //     type: Number,
    //     required: false,
    //     default: 1,
    // },
}>()

const productListFilterStore = useProductListFilterStore()
const dynamicFiltersCount = ref(useDevice().isMobile ? 2 : 10)
const filtersContainer = ref<HTMLElement>()
const bookmark = ref<HTMLElement>()
const productListFilters = ref<HTMLElement>()

const visibleFiltersCount = computed(() => {
    return productListFilterStore.showMoreFilters ? 999 : dynamicFiltersCount.value
})

const updateBookmarkHeight = () => {
    nextTick(() => {
        if (!bookmark.value || !productListFilters.value) return

        const filtersHeight = productListFilters.value.offsetHeight
        bookmark.value.style.height = `${filtersHeight}px`
    })
}

const calculateVisibleFilters = () => {
    nextTick(() => {
        if (!filtersContainer.value || !props.sidebarFilters) return

        const containerWidth = filtersContainer.value.offsetWidth
        let totalWidth = 0
        let visibleCount = 0
        const gap = 8 // CSS gap value

        // Temporarily show all filters to measure them
        const filterElements = filtersContainer.value.querySelectorAll('.product-list-filters__filter')
        const moreFiltersButton = filtersContainer.value.querySelector('.product-list-filters__more-filters-button') as HTMLElement
        const originalDisplayValues: string[] = []

        // Store original display values and show all elements
        filterElements.forEach((element, index) => {
            const htmlElement = element as HTMLElement
            originalDisplayValues[index] = htmlElement.style.display
            htmlElement.style.display = 'block'
        })

        // Reserve space for "více filtrů" button if we have more than 1 filter
        let buttonWidth = 0
        if (moreFiltersButton && props.sidebarFilters.length > 1) {
            moreFiltersButton.style.display = 'flex'
            buttonWidth = moreFiltersButton.offsetWidth + gap
        }

        const availableWidth = containerWidth - buttonWidth

        // Calculate how many fit in one row (accounting for button space)
        for (let i = 0; i < filterElements.length; i++) {
            const filterElement = filterElements[i] as HTMLElement
            const filterWidth = filterElement.offsetWidth

            if (totalWidth + filterWidth + (i > 0 ? gap : 0) <= availableWidth) {
                totalWidth += filterWidth + (i > 0 ? gap : 0)
                visibleCount++
            } else {
                break
            }
        }

        // Restore original display values
        filterElements.forEach((element, index) => {
            const htmlElement = element as HTMLElement
            htmlElement.style.display = originalDisplayValues[index] || ''
        })

        if (moreFiltersButton) {
            moreFiltersButton.style.display = ''
        }

        dynamicFiltersCount.value = Math.max(1, visibleCount)
        updateBookmarkHeight()
    })
}

const inputSearch = ref<{ [name: number]: string }>({})

const filteredList = (filter: Filter, id: number): FilterOption[] => {
    const options =
        filter.options?.map((option: FilterOption) => {
            option.url = option.url.replace(/(&|)page=\d+/, '')

            return option
        }) || []

    if (inputSearch.value[id]) {
        const searchTerm = removeDiacritics(inputSearch.value[id])
        return options.filter((post: any): boolean => {
            const optionTitle = removeDiacritics(post.title)
            return optionTitle.includes(searchTerm)
        })
    } else {
        return options
    }
}

const canBeAnchorTag = (url: string): boolean => {
    const regex = /(priceFrom=|priceTo=|vt\[0\]=|minRating=)/
    const slashCount = (url.match(/\//g) || []).length

    return !regex.test(url) && slashCount < 4
}

const redirectToUrl = async (url: string) => {
    // Save scroll position of opened filter before navigation
    const openedFilterBody = document.querySelector('.product-list-filters__content--opened .product-list-filters__body')
    if (openedFilterBody) {
        productListFilterStore.saveScrollPosition(openedFilterBody.scrollTop)
    }

    // if (useDevice().isMobile && optionId) {
    //     url = `${url}#option-${optionId}`
    // } else if (!useDevice().isMobile && focusOnList) {
    url = `${url}#product-list-filters`
    // }

    await safeNavigateTo(url)
}

const hasValidRatingItems = computed((): boolean => {
    const filters = props.sidebarFilters
    let hasValidRatingItems = false

    filters.forEach((filter: Filter) => {
        if (filter.type !== 'minRating') {
            return
        }
        const items = filter.items ?? []
        items.forEach((item) => {
            if (item.count > 0) {
                hasValidRatingItems = true
            }
        })
    })

    return hasValidRatingItems
})

const adjustMobileFilterPosition = () => {
    if (!useDevice().isMobile) return

    nextTick(() => {
        const contentElements = document.querySelectorAll('.product-list-filters__content--opened')
        contentElements.forEach((element: Element) => {
            const htmlElement = element as HTMLElement
            const rect = htmlElement.getBoundingClientRect()
            // const viewportWidth = window.innerWidth

            // Calculate the distance from the left edge of the screen
            const leftOffset = rect.left

            // Position at the beginning of the screen (with small margin)
            if (leftOffset !== 7) {
                htmlElement.style.left = `${-leftOffset + 7}px`
                // htmlElement.style.width = `${Math.min(300, viewportWidth - 30)}px`
            }
        })
    })
}

const toggleMoreFilters = () => {
    productListFilterStore.showMoreFilters = !productListFilterStore.showMoreFilters
    updateBookmarkHeight()
}

const toggleFilter = (filterTitle: string) => {
    productListFilterStore.openFilterTitle = productListFilterStore.openFilterTitle === filterTitle ? null : filterTitle
    adjustMobileFilterPosition()
}

const closeFilters = (event: Event) => {
    const target = event.target as HTMLElement
    if (!target.closest('.product-list-filters__filter')) {
        productListFilterStore.openFilterTitle = null
    }
}

const resizeHandler = () => {
    calculateVisibleFilters()
    adjustMobileFilterPosition()
}

const activeFilter = (filter: Filter): number => {
    return (
        filter.options?.reduce((count, item) => {
            return item.isActive ? count + 1 : count
        }, 0) || 0
    )
}

onMounted(() => {
    document.addEventListener('click', closeFilters)
    window.addEventListener('resize', resizeHandler)
    resizeHandler()

    // Restore scroll position after mount
    nextTick(() => {
        const openedFilterBody = document.querySelector('.product-list-filters__content--opened .product-list-filters__body')
        if (openedFilterBody && productListFilterStore.scrollPosition > 0) {
            openedFilterBody.scrollTop = productListFilterStore.restoreScrollPosition()
        }
    })
})

onUnmounted(() => {
    document.removeEventListener('click', closeFilters)
    window.removeEventListener('resize', resizeHandler)
})

const isFilterSticky = ref(false)

const checkPosition = () => {
    const contentHeader = document.querySelector('.product-list-filters__bookmark2')

    if (contentHeader) {
        const rect = contentHeader.getBoundingClientRect()
        const isMobile = window.innerWidth < 768
        const viewportMiddle = document.body.classList.contains('header-sticky') ? (isMobile ? 0 : 0) : isMobile ? 71 : 111
        const elementTop = rect.top
        const isAtOrAboveMiddle = elementTop <= viewportMiddle

        // Show sticky header when original header center is at or above viewport middle
        isFilterSticky.value = isAtOrAboveMiddle
    }
}

const { throttledFunction: throttledCheckPosition } = useThrottle(checkPosition, 200)

onMounted(() => {
    window.addEventListener('scroll', throttledCheckPosition)
    window.addEventListener('resize', throttledCheckPosition)
    setTimeout(() => {
        checkPosition()
    }, 500)
})

onUnmounted(() => {
    window.removeEventListener('scroll', throttledCheckPosition)
    window.removeEventListener('resize', throttledCheckPosition)
})
</script>

<style lang="scss" scoped>
$bg: $color-secondary-cream;
.product-list-filters {
    width: 100vw;
    margin-left: calc(-50vw + 50%);
    background: $bg;
    padding: 8px 0;

    @include screen-md {
        padding: 8px 10px;
    }

    &--sticky {
        @include screen-md {
            position: fixed;
            top: 0;
            z-index: 10;
            left: 0;
            right: 0;
            transition: transform 0.5s;
            transform: translateY($headerHeightMobile);
        }

        @include screen-md {
            display: flex;
            transform: translateY($headerHeightDesktop);
        }
    }

    &__bookmark {
        transition: height 0.3s ease;
        display: none;

        @include screen-md {
            &--sticky {
                display: block;
            }
        }
    }

    &__container {
        width: min(calc(100% - 14px), $max-width-container-inside + (2 * $padding-container-inside));
        margin: 0 auto;
        display: grid;
        grid-template-columns: 1fr;

        @include screen-md {
            display: grid;
            grid-template-columns: 1fr max-content;
        }
    }

    &__filters {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        gap: 8px 3px;
        align-items: center;
    }

    &__filter {
        position: relative;
        display: none;

        &--visible {
            display: block;
        }
    }

    &__content {
        display: none;
        position: absolute;
        top: calc(100% - 2px);
        left: 0;
        width: calc(100vw - 14px);
        background: #fff;
        border: 1px solid #ddd;
        border-radius: 0px 8px 8px 8px;
        padding: 10px;
        //box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        z-index: 10;

        @include screen-md {
            width: 300px;
        }

        &--opened {
            display: block;
        }
    }

    &__title {
        padding: 0px 6px;
        border-radius: 50px;
        cursor: pointer;
        display: flex;
        align-items: center;
        border: 1px solid $bg;
        font-size: 13px;
        color: $color-primary-green;

        @include screen-md {
            padding: 0px 10px;
        }

        &:after {
            content: '';
            display: inline-block;
            width: 7px;
            height: 7px;
            margin-left: 10px;
            border-bottom: 2px solid $color-secondary-dark-green-1;
            border-right: 2px solid $color-secondary-dark-green-1;
            transform: rotate(45deg);
            transition: transform 0.3s ease;
            position: relative;
            top: -1px;
        }

        &:hover {
            @include screen-md {
                background: #fff;

                &:after {
                    border-bottom: 2px solid $color-secondary-dark-green-2;
                    border-right: 2px solid $color-secondary-dark-green-2;
                }
            }
        }

        &--active {
            background: #fff;
            border: 1px solid #ddd;
            border-bottom: 0;
            border-radius: 18px 18px 0 0;
            z-index: 11;
            position: relative;
        }

        &__text {
            padding-top: 7px;
            padding-bottom: 7px;

            @include screen-md {
                padding-top: 9px;
                padding-bottom: 9px;
            }
        }

        &__count {
            background: $color-primary-pink;
            padding: 2px 5px;
            border-radius: 10px;
            margin-left: 5px;
        }
    }

    &__search-input {
        width: 100%;
        background: url('/nuxt-assets/icons/search.svg') no-repeat 10px center;
        border: 1px solid $color-grey-300;
        border-radius: 50px;
        padding: 8px 10px 8px 35px;
        margin-bottom: 10px;
    }

    &__body {
        max-height: 300px;
        overflow-y: auto;
    }

    &__more-filters-button {
        color: $color-secondary-dark-green-2;
        text-decoration: underline;
        font-size: 13px;
        display: flex;
        align-items: center;
        cursor: pointer;
        //margin-left: auto;

        &:hover {
            text-decoration: none;
        }

        &:after {
            content: '';
            display: inline-block;
            width: 7px;
            height: 7px;
            margin-left: 10px;
            border-bottom: 2px solid $color-secondary-dark-green-2;
            border-right: 2px solid $color-secondary-dark-green-2;
            transform: rotate(45deg);
            transition: transform 0.3s ease;
            position: relative;
            top: -1px;
        }

        &--arrow-up:after {
            transform: rotate(-135deg);
            position: relative;
            top: 2px;
        }
    }
}

.pageWrap--with-headline-bar .product-list-filters--sticky {
    transform: translateY(#{$headerHeightMobile + $headlineBarHeightMobile});

    @include screen-md {
        transform: translateY(#{$headerHeightDesktop + $headlineBarHeightDesktop});
    }
}

.header-sticky .product-list-filters--sticky {
    @include screen-md {
        transform: translateY(0px);
    }
}
</style>
